import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Context, getBexApiContext } from 'bernie-context';

import { ShortlistToggle } from '@shared-ui/trip-planning-travelpulse';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { ShortlistStore } from 'stores/shortlist-store';
import { UIPageStore } from 'stores/ui-page-store';

import { PriceViewModel, ReviewsViewModel, ShortlistModel } from 'models';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { UitkFavoriteSwitch } from 'uitk-react-switch';

import { ProductType } from '__generated__/api';

const TrackedFavoriteSwitch = ClickTracker(UitkFavoriteSwitch);

export interface FavoriteButtonProps {
  category: string;
  shortlistModel?: ShortlistModel;
  shortlist?: ShortlistStore;
  analytics?: AnalyticsStore;
  context?: Context;
  labelSave: string;
  labelRemove: string;
  uiPage?: UIPageStore;
  onSaveMessage?: string;
  id: string;
  referralObject?: {
    favorite?: {
      rfrr: string;
      linkName?: string;
    };
    unfavorite?: {
      rfrr: string;
      linkName?: string;
    };
  };
  rfrr?: string;
  onClick?: () => void;
  routeStore?: RouteStore;
  currentHotel?: CurrentHotelStore;
  price?: PriceViewModel;
  stars?: string;
  reviews?: ReviewsViewModel;
}

@inject('shortlist', 'context', 'analytics', 'uiPage', 'routeStore')
@observer
export class FavoriteButton extends React.Component<FavoriteButtonProps> {
  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
  }

  public handleClick() {
    const { shortlist, context, uiPage, shortlistModel, category, onClick, onSaveMessage } = this.props;

    const meta = shortlistModel && shortlistModel.meta;
    const hotelId = meta && meta.hotelId && Number(meta.hotelId);
    const favoritedLocallyVal =
      shortlist[category] && shortlist[category].favoritedLocally && shortlist[category].favoritedLocally[hotelId];
    const storeVal = this.existInStores();
    const initialFavState = typeof favoritedLocallyVal !== 'boolean' || favoritedLocallyVal === storeVal ? storeVal : favoritedLocallyVal;
    const updatedFavState = !initialFavState;

    if (shortlist) {
      if (typeof shortlist.updateFavoritedLocally === 'function') {
        shortlist.updateFavoritedLocally(hotelId, updatedFavState, 'hotel');
      }

      if (typeof shortlist.fetch === 'function') {
        const url = updatedFavState ? shortlistModel && shortlistModel.saveUrl : shortlistModel && shortlistModel.removeUrl;
        shortlist.fetch(url, context, meta, category);
      }
    }
    if (updatedFavState && uiPage && typeof uiPage.showNotification === 'function') {
      uiPage.showNotification(onSaveMessage);
      liveAnnounce(onSaveMessage, Level.POLITE);
    }

    this.fireTrackingEvent(updatedFavState);
    if (typeof onClick === 'function') {
      this.props.onClick();
    }
  }

  private handleOnChange(checked: boolean, prevChecked: boolean): void {
    if (!checked && prevChecked && this.props.uiPage && typeof this.props.uiPage.showNotification === 'function') {
      this.props.uiPage.showNotification(this.props.onSaveMessage);
      liveAnnounce(this.props.onSaveMessage, Level.POLITE);
    }
  }

  public shouldComponentUpdate(nextProps): boolean {
    return this.props !== nextProps;
  }

  private fireTrackingEvent(isFavorited) {
    const { shortlist, analytics, shortlistModel } = this.props;
    const category = 'hotel';

    if (shortlist && shortlist[category] && shortlist[category].favorited) {
      const trackingDataNumberOfHearted = {
        rfrr: `FavoritedHotels.${shortlist[category].favorited.length}`,
        linkName: 'Number of favorited hotels'
      };
      let UISPrimeSchema = null;
      let events = null;
      if (shortlistModel && shortlistModel.clickTracking && shortlistModel.clickTracking.length > 0) {
        UISPrimeSchema = toJS(shortlistModel.clickTracking);
        events = isFavorited ? [{ name: 'ShortlistSaved' }] : [{ name: 'ShortlistUnsaved' }];
      }
      analytics.trackEvent(
        trackingDataNumberOfHearted.rfrr,
        trackingDataNumberOfHearted.linkName,
        analytics.omnitureData,
        events,
        true,
        UISPrimeSchema
      );
    }
  }

  public getReferrals() {
    const referralObject = this.props.referralObject || {};
    const defaultFavorite = {
      rfrr: 'Scratchpad.SaveFavorite',
      linkName: 'Add to favorite lists'
    };
    const defaultUnfavorite = {
      rfrr: 'Scratchpad.RemoveFavorite',
      linkName: 'Remove from favorite lists'
    };

    return {
      ...referralObject,
      favorite: {
        ...defaultFavorite,
        ...referralObject.favorite
      },
      unfavorite: {
        ...defaultUnfavorite,
        ...referralObject.unfavorite
      }
    };
  }

  private existsInShortlist() {
    const { shortlist, category, shortlistModel } = this.props;
    const meta = shortlistModel && shortlistModel.meta;
    const hotelId = meta && meta.hotelId && Number(meta.hotelId);
    const favorited = shortlist && shortlist[category] && shortlist[category].favorited;

    return favorited && favorited.some(e => e.id === hotelId);
  }

  private existInStores(): boolean {
    const { shortlistModel } = this.props;

    return this.existsInShortlist() || (shortlistModel && shortlistModel.favorited);
  }

  public render() {
    const { category, context, id, shortlist, shortlistModel } = this.props;
    const meta = shortlistModel && shortlistModel.meta;
    const metadata = Object.entries(meta).map(kv => ({
      key: kv[0],
      value: kv[1]
    }));
    const inputs = {
      context: getBexApiContext(context),
      metadata,
      productId: meta && meta.hotelId,
      productType: ProductType.PROPERTY_DETAILS
    };
    const referralObject = this.getReferrals();
    const hotelId = meta && meta.hotelId && Number(meta.hotelId);
    const favoritedLocallyVal = shortlist[category].favoritedLocally && shortlist[category].favoritedLocally[hotelId];
    const storeVal = this.existInStores();
    // Check for local favorited value. If nonexistent or equal to the model value, use model value.
    // If not use local value as first priority.
    const isFavorited = typeof favoritedLocallyVal !== 'boolean' || favoritedLocallyVal === storeVal ? storeVal : favoritedLocallyVal;
    const label = isFavorited ? this.props.labelRemove : this.props.labelSave;
    const rfrr = isFavorited ? referralObject.unfavorite.rfrr : referralObject.favorite.rfrr;
    const linkName = isFavorited ? referralObject.unfavorite.linkName : referralObject.favorite.linkName;

    return (
      <AbacusExperiment name="PWA_Shared_UI_Shortlist_Toggle">
        <AbacusControl>
          <TrackedFavoriteSwitch id={id} label={label} checked={isFavorited} onChange={this.handleClick} rfrr={rfrr} linkName={linkName} />
        </AbacusControl>
        <AbacusVariant bucket={1}>
          <ShortlistToggle inputs={inputs} initialChecked={shortlistModel && shortlistModel.favorited} onChange={this.handleOnChange} />
        </AbacusVariant>
      </AbacusExperiment>
    );
  }
}
